import type {
  BlockStateDefinition,
  ComponentType,
} from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import type {
  CopilotBlockRef,
  CopilotBlockStateType,
  CopilotComponentType,
} from '@unifyapps/defs/blocks/Copilot/types';
import { ChatVariants, CopilotBlockMethods } from '@unifyapps/defs/blocks/Copilot/types';
import type { Action } from '@unifyapps/defs/types/action';
import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import { lazy } from 'react';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import { ENTIRE_ENTITY } from '@unifyapps/carbon/no-code/dependency/const';
import { ComputeContextKeys } from '@unifyapps/carbon/no-code/hooks/computeContext/const';

const importCopilotComponent = () =>
  import(
    /* webpackChunkName: "copilot-block" */
    './Copilot'
  );

const Copilot = lazy(importCopilotComponent);

class CopilotBlockStateDefinition implements BlockStateDefinition {
  useBlockRef?: boolean | undefined = true;

  get type(): ComponentTypeUnionType {
    return 'Copilot';
  }

  get initialStateGetter() {
    return (block: BlockType) => ({
      id: block.id,
      ..._pick(block.component as CopilotComponentType, ['content', 'appearance', 'componentType']),
      aiAgent: {},
    });
  }

  getComponent() {
    return Copilot as ComponentType;
  }

  getSlotOrder(): { order: string[] } {
    return {
      order: ['messageHistory', 'messageInputPanel', 'agentDetails'],
    };
  }

  getImplicitDependencies(block: BlockType<CopilotComponentType>): DependsOnType[] | undefined {
    const messageInputPanelBlockId = block.component.slots?.messageInputPanel?.blockId;
    if (!messageInputPanelBlockId) return;

    return [
      {
        id: messageInputPanelBlockId,
        p: [ENTIRE_ENTITY],
      },
      {
        id: ComputeContextKeys.PAGE_INPUTS,
        p: [`${ComputeContextKeys.PAGE_INPUTS}['configType']`],
      },
    ];
  }

  getBlockControlMethods() {
    return [
      {
        id: CopilotBlockMethods.SendMessage,
        label: 'Send Message',
        method: (block: BlockStateUnionType, action: Action, blockRef?: CopilotBlockRef) => {
          const methodPayload = (action.payload?.methodPayload ?? {}) as {
            message?: string;
            attachments?: FileType[];
            // if we want to use a different model for a specific message, we can pass the modelId in the payload
            modelId?: string;
            // if we want to enable web search for the agent used.
            webSearchEnabled?: boolean;
          };

          const content = (block as CopilotBlockStateType).content;

          /**
           * Restricting message sending ability to only when
           * an AI-agent is selected for chat variant AIAgent
           * OR
           * chat variant is not defined (for backward compatibility since chat variant would not be present in older blocks)
           * OR
           * a live chat config is selected for chat variant live chat
           */
          const isValidAIAgent =
            (!content.variant || content.variant === ChatVariants.AIAgent) &&
            Boolean(content.aiAgentId);

          const shouldSend = content.variant === ChatVariants.LiveChat || isValidAIAgent;

          if (shouldSend) {
            if (methodPayload.message || methodPayload.attachments?.length) {
              blockRef?.submitMessage?.({
                message: methodPayload.message,
                attachments: methodPayload.attachments,
                createCaseParams: methodPayload.modelId
                  ? {
                      settings: {
                        taskExecutionModel: methodPayload.modelId,
                      },
                      defaultTools: { webSearch: Boolean(methodPayload.webSearchEnabled) },
                    }
                  : undefined,
              });
            }

            return Promise.resolve();
          }

          return {
            ...block,
            errorMessage: 'Please select an AI Agent ID',
          };
        },
      },
      {
        id: CopilotBlockMethods.CreateNewChat,
        label: 'Create New Chat',
        method: (_: BlockStateUnionType, __: Action, blockRef?: CopilotBlockRef) => {
          blockRef?.createNewChat?.();

          return Promise.resolve();
        },
      },
      {
        id: CopilotBlockMethods.GoToChat,
        label: 'Go to Chat',
        method: (_: BlockStateUnionType, action: Action, blockRef?: CopilotBlockRef) => {
          const methodPayload = action.payload?.methodPayload as { chatId: string };
          blockRef?.goToChat?.(methodPayload.chatId);

          return Promise.resolve();
        },
      },
      {
        id: CopilotBlockMethods.StopResponseGeneration,
        label: 'Stop Response Generation',
        method: (_: BlockStateUnionType, __: Action, blockRef?: CopilotBlockRef) => {
          blockRef?.onStopResponse?.();

          return Promise.resolve();
        },
      },
    ];
  }
}

export default CopilotBlockStateDefinition;
